import Durations from '@omni/kit/constants/Durations';
import { createAnimation } from '@omni/kit/theming/Animating';
import React, {
  PropsWithChildren,
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Animated, StyleProp, View, ViewStyle } from 'react-native';

export interface KitFadeViewProps {
  show?: boolean;
  /**
   * Replaces the hidden element with a View of equivalent size to prevent
   * layout shifting.
   * @default false
   */
  keepElementSize?: boolean;
  animationDuration?: number;
  style?: StyleProp<ViewStyle>;
}

export default function KitFadeView({
  show = false,
  keepElementSize = false,
  animationDuration = Durations.s,
  children,
  style,
}: PropsWithChildren<KitFadeViewProps>): ReactElement | null {
  const [displayNone, setDisplayNone] = useState(!show);
  const [startingHeight, setStartingHeight] = useState(0);
  const [startingWidth, setStartingWidth] = useState(0);
  const opacity = useRef(new Animated.Value(show ? 1 : 0)).current;
  const fadeIn = useRef(createAnimation(opacity, 1, animationDuration)).current;
  const fadeOut = useRef(
    createAnimation(opacity, 0, animationDuration)
  ).current;

  useEffect(() => {
    if (show) {
      setDisplayNone(false);
      fadeIn.start();
    } else {
      fadeOut.start(({ finished }) => {
        if (finished) setDisplayNone(true);
      });
    }
  }, [fadeIn, fadeOut, opacity, show]);

  const replacementElement = useMemo(() => {
    return <View style={{ width: startingWidth, height: startingHeight }} />;
  }, [startingWidth, startingHeight]);

  return displayNone ? (
    keepElementSize ? (
      replacementElement
    ) : null
  ) : (
    <Animated.View
      onLayout={({ nativeEvent }) => {
        if (keepElementSize) {
          setStartingHeight(nativeEvent.layout.height);
          setStartingWidth(nativeEvent.layout.width);
        }
      }}
      style={[style, { opacity: opacity }]}
    >
      {children}
    </Animated.View>
  );
}
